import {
  IMPORT_PRESETS,
  IMPORT_TEMPLATE_COLUMNS,
  MAPPABLE_FIELD_LABELS,
  MAPPABLE_FIELDS,
  parseCsv,
  suggestMapping,
  type ImportDto,
  type ImportPreviewResponse,
} from '@performance-crm/shared';
import { Download, FileUp, Loader2, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { fmt } from '@/lib/format';
import {
  fetchImportRows,
  useAdvisors,
  useCatalogs,
  useImportPreview,
  useImports,
  useProjects,
  useRunImport,
} from '@/lib/queries';
import { downloadLeadTemplate, fileToCsv } from '@/lib/spreadsheet';
import { downloadImportDetail } from './importExport';
import { ImportRetryDialog } from './ImportRetryDialog';

/** Valores fijos para toda la carga cuando la fila no trae la columna (volantes, eventos, bases compradas). */
interface Defaults {
  medium: string;
  entryChannel: string;
  utmCampaign: string;
  /** yyyy-mm-dd del input date; se envía como dd/mm/yyyy. */
  capturedAt: string;
}
const EMPTY_DEFAULTS: Defaults = { medium: '', entryChannel: '', utmCampaign: '', capturedAt: '' };

const toIsoDate = (d: string) => {
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

export function ImportTab({ companyId }: { companyId: string }) {
  const { data: projects } = useProjects();
  const { data: advisors } = useAdvisors(companyId);
  const { data: catalogs } = useCatalogs(companyId);
  const { data: imports } = useImports(companyId);
  const preview = useImportPreview();
  const run = useRunImport();
  const toast = useToast();
  const [csv, setCsv] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [sheetName, setSheetName] = useState<string | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [retryFor, setRetryFor] = useState<ImportDto | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [advisorId, setAdvisorId] = useState('');
  const [defaults, setDefaults] = useState<Defaults>(EMPTY_DEFAULTS);
  const [fieldMap, setFieldMap] = useState<Record<string, string> | null>(null);
  const [source, setSource] = useState('csv');
  const [result, setResult] = useState<ImportPreviewResponse | null>(null);
  const preset = IMPORT_PRESETS.find((p) => p.source === source) ?? IMPORT_PRESETS[0]!;

  const humanAdvisors = useMemo(
    () => (advisors ?? []).filter((a) => a.kind === 'HUMAN'),
    [advisors],
  );
  /** Defaults enviados: los del preset (Evolta / Sperant) + los elegidos en pantalla (solo los no vacíos). */
  const mergedDefaults = useMemo(() => {
    const out: Record<string, string> = { ...preset.defaults };
    if (defaults.medium) out.medium = defaults.medium;
    if (defaults.entryChannel) out.entryChannel = defaults.entryChannel;
    if (defaults.utmCampaign.trim()) out.utmCampaign = defaults.utmCampaign.trim();
    const d = toIsoDate(defaults.capturedAt);
    if (d) out.capturedAt = d;
    return out;
  }, [preset, defaults]);

  /** Con un preset (Evolta / Sperant) mandan sus columnas; con el genérico, la sugerencia por cabeceras. */
  const mapFor = (headers: readonly string[], src: string) => {
    const suggested = suggestMapping(headers) as Record<string, string>;
    const pr = IMPORT_PRESETS.find((p) => p.source === src);
    return pr && Object.keys(pr.fieldMap).length ? { ...suggested, ...pr.fieldMap } : suggested;
  };

  /** Lee el archivo (o una hoja concreta de un Excel con varias) y rearma el mapeo por cabeceras. */
  const loadFile = async (f: File, sheet?: string) => {
    setReading(true);
    try {
      const read = await fileToCsv(f, sheet);
      const { headers } = parseCsv(read.csv);
      if (!headers.length) throw new Error('No se encontró una fila de cabecera');
      setCsv(read.csv);
      setFile(f);
      setFileName(f.name);
      setSheetName(read.sheetName);
      setSheetNames(read.sheetNames);
      if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
      setFieldMap(mapFor(headers, source));
    } catch (err) {
      toast.error('No se pudo leer el archivo', (err as Error).message);
    } finally {
      setReading(false);
    }
  };
  const onFile = async (f: File | undefined) => {
    if (f) await loadFile(f);
  };

  const exportDetail = async (imp: ImportDto) => {
    setExporting(imp.id);
    try {
      await downloadImportDetail(imp, await fetchImportRows(imp.id));
    } catch (err) {
      toast.error('No se pudo exportar el detalle', (err as Error).message);
    } finally {
      setExporting(null);
    }
  };

  // Cualquier cambio (archivo, columnas, proyecto, asesor o valores por defecto) refresca la vista previa.
  const previewKey = JSON.stringify({ fieldMap, projectId, advisorId, mergedDefaults });
  useEffect(() => {
    if (!csv || !fieldMap) return;
    let cancelled = false;
    preview
      .mutateAsync({
        companyId,
        csv,
        fieldMap,
        defaults: mergedDefaults,
        projectId: projectId || null,
        advisorId: advisorId || null,
      })
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch((err: Error) => {
        if (!cancelled) toast.error('No se pudo actualizar la vista previa', err.message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csv, previewKey, companyId]);

  const start = async () => {
    if (!fieldMap) return;
    try {
      await run.mutateAsync({
        companyId,
        csv,
        name: name.trim() || fileName || 'Importación',
        source,
        fieldMap,
        defaults: mergedDefaults,
        projectId: projectId || null,
        advisorId: advisorId || null,
      });
      toast.success(
        'Importación iniciada',
        'Se procesa en segundo plano; el progreso aparece abajo.',
      );
      setCsv('');
      setResult(null);
      setFile(null);
      setFileName('');
      setSheetName(null);
      setSheetNames([]);
      setFieldMap(null);
      setName('');
    } catch (err) {
      toast.error('No se pudo iniciar', (err as Error).message);
    }
  };
  const issues = result?.sample.filter((s) => s.preview.issues.length > 0).length ?? 0;
  const companyProjects = projects?.filter((p) => p.companyId === companyId) ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Carga masiva de leads (Excel o CSV)</CardTitle>
              <CardDescription>
                Volantes, eventos corporativos, bases de datos o exportaciones de otro CRM. Cada
                fila pasa por la misma identidad (DNI → celular → correo) que un lead nuevo: si la
                persona ya existe, entra como recontacto y no se duplica.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void downloadLeadTemplate()}
              data-testid="download-template"
            >
              <Download className="h-4 w-4" /> Descargar plantilla Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="csv-file">Archivo (.xlsx, .xls o .csv)</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".xlsx,.xlsm,.xls,.ods,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={(e) => void onFile(e.target.files?.[0])}
                data-testid="csv-input"
              />
              {file && sheetNames.length > 1 ? (
                <div className="space-y-1">
                  <Label htmlFor="csv-sheet" className="text-xs">
                    Hoja del Excel ({sheetNames.length} hojas)
                  </Label>
                  <Select
                    id="csv-sheet"
                    className="h-9"
                    value={sheetName ?? ''}
                    onChange={(e) => void loadFile(file, e.target.value)}
                    data-testid="import-sheet"
                  >
                    {sheetNames.map((sh) => (
                      <option key={sh} value={sh}>
                        {sh}
                      </option>
                    ))}
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    Se eligió la hoja con más columnas reconocidas; cámbiala si no es la correcta.
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">
                  Cabecera en la primera fila.
                  {sheetName
                    ? ` Se lee la hoja "${sheetName}".`
                    : ' De un Excel con varias hojas podrás elegir cuál.'}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="csv-source">Sistema de origen</Label>
              <Select
                id="csv-source"
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  if (csv) setFieldMap(mapFor(parseCsv(csv).headers, e.target.value));
                }}
                data-testid="import-preset"
              >
                {IMPORT_PRESETS.map((p) => (
                  <option key={p.source} value={p.source}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <p className="text-muted-foreground text-xs">{preset.description}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="import-name">Nombre de la carga</Label>
              <Input
                id="import-name"
                value={name}
                placeholder="Ej. Volanteo Jesús María 05/09"
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Asignación</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="csv-project">Proyecto por defecto</Label>
                <Select
                  id="csv-project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  data-testid="import-project"
                >
                  <option value="">— (usar la columna "Proyecto" de cada fila)</option>
                  {companyProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="csv-advisor">Asesor por defecto</Label>
                <Select
                  id="csv-advisor"
                  value={advisorId}
                  onChange={(e) => setAdvisorId(e.target.value)}
                  data-testid="import-advisor"
                >
                  <option value="">— (por turnos del proyecto)</option>
                  {humanAdvisors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.fullName}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Prioridad: columna "Asesor" de la fila (usuario, correo o nombre) → asesor por defecto
              → reparto automático del proyecto. Si la persona ya tiene un lead activo en el
              proyecto, se anota el recontacto en ese lead y conserva a su asesor (si estaba en
              bolsa, lo toma el asesor indicado).
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">
              Valores por defecto (cuando la columna viene vacía)
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1">
                <Label htmlFor="import-medium">Medio de captación</Label>
                <Select
                  id="import-medium"
                  value={defaults.medium}
                  onChange={(e) => setDefaults({ ...defaults, medium: e.target.value })}
                  data-testid="import-medium"
                >
                  <option value="">— (columna "Medio")</option>
                  {catalogs?.media.map((m) => (
                    <option key={m.name} value={m.name}>
                      {m.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="import-channel">Canal de entrada</Label>
                <Select
                  id="import-channel"
                  value={defaults.entryChannel}
                  onChange={(e) => setDefaults({ ...defaults, entryChannel: e.target.value })}
                >
                  <option value="">—</option>
                  {catalogs?.entryChannels.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="import-campaign">Campaña / evento</Label>
                <Input
                  id="import-campaign"
                  value={defaults.utmCampaign}
                  placeholder="Ej. Feria Expo Urbano 2026"
                  onChange={(e) => setDefaults({ ...defaults, utmCampaign: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="import-date">Fecha de captación</Label>
                <Input
                  id="import-date"
                  type="date"
                  value={defaults.capturedAt}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDefaults({ ...defaults, capturedAt: e.target.value })}
                />
              </div>
            </div>
          </div>

          {(reading || preview.isPending) && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />{' '}
              {reading ? 'Leyendo el archivo…' : 'Analizando…'}
            </p>
          )}
          {result && fieldMap && (
            <>
              <p className="text-sm">
                <span className="font-medium">{result.totalRows} filas</span> ·{' '}
                {result.headers.length} columnas
                {issues > 0 && (
                  <Badge tone="warning" className="ml-2">
                    {issues} de {result.sample.length} filas de muestra con problemas
                  </Badge>
                )}
              </p>
              <details className="rounded-md border">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                  Columnas detectadas ({Object.keys(fieldMap).length} mapeadas) · ajustar
                </summary>
                <div className="grid gap-2 border-t p-3 sm:grid-cols-3 lg:grid-cols-4">
                  {MAPPABLE_FIELDS.filter((f) => f !== 'documentType').map((f) => (
                    <div key={f} className="space-y-1">
                      <Label className="text-xs">{MAPPABLE_FIELD_LABELS[f]}</Label>
                      <Select
                        value={fieldMap[f] ?? ''}
                        className="h-9"
                        aria-label={`columna ${f}`}
                        onChange={(e) => {
                          const next = { ...fieldMap };
                          if (e.target.value) next[f] = e.target.value;
                          else delete next[f];
                          setFieldMap(next);
                        }}
                      >
                        <option value="">—</option>
                        {result.headers.map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </Select>
                    </div>
                  ))}
                  {(catalogs?.customFields ?? []).map((f) => {
                    const k = `custom.${f.key}`;
                    return (
                      <div key={k} className="space-y-1">
                        <Label className="text-xs">Campo: {f.label}</Label>
                        <Select
                          value={fieldMap[k] ?? ''}
                          className="h-9"
                          aria-label={`columna ${k}`}
                          onChange={(e) => {
                            const next = { ...fieldMap };
                            if (e.target.value) next[k] = e.target.value;
                            else delete next[k];
                            setFieldMap(next);
                          }}
                        >
                          <option value="">—</option>
                          {result.headers.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </details>
              {result.sample.some((s) => s.preview.identity.doNotContact) && (
                <p
                  className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs dark:bg-amber-950/30"
                  data-testid="import-dnc-hint"
                >
                  {result.sample.filter((s) => s.preview.identity.doNotContact).length} de las filas
                  mostradas son personas que pidieron que no les escribamos: se cargan sin
                  consentimiento, no se les escribe por WhatsApp y el bot no las atiende. Una carga
                  nunca levanta un «no contactar».
                </p>
              )}
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-xs" data-testid="import-preview">
                  <thead className="bg-muted/50 text-muted-foreground text-left uppercase">
                    <tr>
                      <th className="px-2 py-1">Nombre</th>
                      <th className="px-2 py-1">Celular / correo</th>
                      <th className="px-2 py-1">Proyecto</th>
                      <th className="px-2 py-1">Asesor</th>
                      <th className="px-2 py-1">Medio</th>
                      <th className="px-2 py-1">Identidad</th>
                      <th className="px-2 py-1">Problemas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.sample.map((s, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1">
                          {[s.preview.mapped.firstName, s.preview.mapped.lastNamePaternal]
                            .filter(Boolean)
                            .join(' ') || '—'}
                        </td>
                        <td className="px-2 py-1">
                          {s.preview.mapped.phone ?? s.preview.mapped.email ?? '—'}
                        </td>
                        <td className="px-2 py-1">{s.preview.projectName ?? '—'}</td>
                        <td className="px-2 py-1">
                          {s.preview.identity.activeLeadInProject &&
                          s.preview.identity.activeLeadAdvisorName
                            ? s.preview.identity.activeLeadAdvisorName
                            : (s.preview.advisorName ?? 'Por turnos')}
                        </td>
                        <td className="px-2 py-1">{s.preview.mapped.medium ?? '—'}</td>
                        <td className="px-2 py-1">
                          {!s.preview.identity.contactId ? (
                            <Badge tone="success">nuevo</Badge>
                          ) : s.preview.identity.activeLeadInProject ? (
                            <Badge tone="warning" title="Ya tiene un lead activo en el proyecto">
                              recontacto · se anota en su lead
                            </Badge>
                          ) : (
                            <Badge tone="warning" title="Lead anterior cerrado o de otro proyecto">
                              recontacto · lead nuevo
                            </Badge>
                          )}
                          {s.preview.identity.doNotContact && (
                            <>
                              {' '}
                              <Badge
                                tone="danger"
                                title="Pidió que no le escribamos: sin WhatsApp ni bot"
                              >
                                no contactar
                              </Badge>
                            </>
                          )}
                        </td>
                        <td className="text-danger px-2 py-1">{s.preview.issues.join(' · ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <Button onClick={start} disabled={run.isPending} data-testid="run-import">
                  <FileUp className="h-4 w-4" /> Importar {result.totalRows} filas
                </Button>
              </div>
            </>
          )}
          {!result && !reading && (
            <p className="text-muted-foreground text-xs">
              Columnas de la plantilla:{' '}
              {IMPORT_TEMPLATE_COLUMNS.map((c) => c.header + (c.required ? '*' : '')).join(', ')}.
              Otras cabeceras también se reconocen (nombre completo, teléfono, DNI, comentario…).
            </p>
          )}
        </CardContent>
      </Card>
      {imports && imports.length > 0 && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
              <tr>
                <th className="px-3 py-2">Importación</th>
                <th className="px-3 py-2">Proyecto / asesor</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-right">Filas</th>
                <th className="px-3 py-2 text-right">Nuevos</th>
                <th className="px-3 py-2 text-right">Recontactos</th>
                <th className="px-3 py-2 text-right">Errores</th>
                <th className="px-3 py-2">Por</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((i) => (
                <tr key={i.id} className="border-t" data-testid="import-row">
                  <td className="px-3 py-2">
                    {i.name}
                    <p className="text-muted-foreground text-xs">{fmt.dateTime(i.createdAt)}</p>
                  </td>
                  <td className="px-3 py-2">
                    {i.projectName ?? 'Por fila'}
                    <p className="text-muted-foreground text-xs">
                      {i.advisorName ?? 'Por turnos / por fila'}
                    </p>
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      tone={
                        i.status === 'COMPLETADO'
                          ? 'success'
                          : i.status === 'ERROR'
                            ? 'danger'
                            : 'primary'
                      }
                    >
                      {i.status === 'EN_PROCESO' ? `${i.processed}/${i.totalRows}` : i.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">{i.totalRows}</td>
                  <td className="px-3 py-2 text-right">{i.created}</td>
                  <td className="px-3 py-2 text-right">{i.recontacts}</td>
                  <td className={`px-3 py-2 text-right ${i.errors ? 'text-danger' : ''}`}>
                    {i.errors > 0 ? (
                      <Link
                        to={`/ingesta?tab=eventos&companyId=${companyId}&importId=${i.id}&status=ERROR`}
                        className="underline"
                        title="Ver las filas con error en Eventos"
                        data-testid="import-errors-link"
                      >
                        {i.errors}
                      </Link>
                    ) : (
                      i.errors
                    )}
                  </td>
                  <td className="px-3 py-2">{i.createdByName ?? '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {i.errors > 0 && (i.status === 'COMPLETADO' || i.status === 'ERROR') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRetryFor(i)}
                          title="Volver a procesar solo las filas con error"
                          data-testid="import-retry"
                        >
                          <RotateCcw className="h-3 w-3" /> Reprocesar errores
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void exportDetail(i)}
                        disabled={exporting === i.id}
                        title="Descargar en Excel el detalle fila por fila"
                        aria-label={`Descargar en Excel el detalle de ${i.name}`}
                        data-testid="import-export"
                      >
                        {exporting === i.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {retryFor && (
        <ImportRetryDialog imp={retryFor} companyId={companyId} onClose={() => setRetryFor(null)} />
      )}
    </div>
  );
}
